import React from 'react';
import { Loader2, AlertCircle, ClipboardList } from 'lucide-react';
import { useTheme } from '../../../core/ui/ThemeContext';
import { useLanguage } from '../../../core/i18n/LanguageContext';
import { formatQuantity } from 'core/utils/units';

/**
 * PurchaseHistoryBlock — Letzte Bestellungen (Phase 3a).
 *
 * Props:
 *   history   Array|null   Ergebnis von getLagerPurchaseHistory
 *   loading   boolean
 *   error     string|null
 *   meins     string       Basismengeneinheit des Materials
 */
const PurchaseHistoryBlock = ({ history, loading, error, meins }) => {
  const { isDarkMode } = useTheme();
  const { t } = useLanguage();

  const cellMuted   = isDarkMode ? 'text-white/45' : 'text-slate-400';
  const cellVal     = isDarkMode ? 'text-white/85' : 'text-slate-800';
  const sectionTitle = `text-xs font-semibold uppercase tracking-wide mb-3 ${isDarkMode ? 'text-white/50' : 'text-slate-400'}`;

  const formatPrice = (price, currency) => {
    if (price == null || price === '') return '—';
    const num = parseFloat(price);
    if (Number.isNaN(num)) return '—';
    const fmt = new Intl.NumberFormat('de-DE', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(num);
    return currency ? `${fmt} ${currency}` : fmt;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    try {
      const d = new Date(dateStr);
      return d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
    } catch {
      return dateStr;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 py-4">
        <Loader2 className={`w-4 h-4 animate-spin ${isDarkMode ? 'text-brand' : 'text-brand'}`} />
        <span className={`text-xs ${cellMuted}`}>{t('lm.purchaseHistoryLoading')}</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center gap-2 py-3">
        <AlertCircle className={`w-4 h-4 flex-shrink-0 ${isDarkMode ? 'text-red-400' : 'text-red-500'}`} />
        <span className={`text-xs ${cellMuted}`}>{t('lm.purchaseHistoryError')}: {error}</span>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center gap-2 mb-3">
        <ClipboardList className={`w-4 h-4 ${isDarkMode ? 'text-brand/70' : 'text-brand'}`} />
        <p className={sectionTitle}>{t('lm.purchaseHistoryTitle')}</p>
      </div>

      {!Array.isArray(history) || history.length === 0 ? (
        <p className={`text-xs ${cellMuted} py-2`}>{t('lm.purchaseHistoryEmpty')}</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-xs border-collapse">
            <thead>
              <tr className={`border-b ${isDarkMode ? 'border-white/10' : 'border-slate-200'}`}>
                <th className={`text-left pb-1.5 pr-3 font-semibold ${cellMuted}`}>{t('lm.purchaseHistoryColEbeln')}</th>
                <th className={`text-left pb-1.5 pr-3 font-semibold ${cellMuted}`}>{t('lm.purchaseHistoryColSupplier')}</th>
                <th className={`text-right pb-1.5 pr-2 font-semibold ${cellMuted}`}>{t('lm.purchaseHistoryColMenge')}</th>
                <th className={`text-right pb-1.5 pr-2 font-semibold ${cellMuted}`}>{t('lm.purchaseHistoryColNetpr')}</th>
                <th className={`text-left pb-1.5 font-semibold ${cellMuted}`}>{t('lm.purchaseHistoryColDate')}</th>
              </tr>
            </thead>
            <tbody>
              {history.map((row, idx) => (
                <tr
                  key={row.ebeln ?? idx}
                  className={`border-b ${isDarkMode ? 'border-white/5' : 'border-slate-50'}`}
                >
                  <td className={`py-1.5 pr-3 font-mono ${cellMuted}`}>{row.ebeln ?? '—'}</td>
                  <td className={`py-1.5 pr-3 ${cellVal}`}>{row.lieferant_name ?? '—'}</td>
                  <td className={`py-1.5 pr-2 text-right ${cellVal}`}>
                    {formatQuantity(row.menge, row.meins ?? meins)}&nbsp;{row.meins ?? meins ?? ''}
                  </td>
                  <td className={`py-1.5 pr-2 text-right ${cellVal}`}>
                    {formatPrice(row.netpr, row.waehrung)}
                  </td>
                  <td className={`py-1.5 ${cellMuted}`}>{formatDate(row.bestelldatum)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default PurchaseHistoryBlock;
